import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  ArrowRightIcon,
  ChevronDownIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
  MapPinIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

import officeImg from '../../assets/img/about/1.jpg';
import { submitFormEmail } from '../../lib/submitFormEmail';

function SectionLabel({ children }) {
  return (
    <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-widest uppercase rounded-full hero-glass-tag text-[#2563EB]">
      {children}
    </span>
  );
}

const contactChannels = [
  {
    id: 'phone',
    label: 'Call Us',
    value: '+977 9705995155',
    sub: '+977 9841586810',
    href: 'tel:+9779705995155',
    icon: PhoneIcon,
  },
  {
    id: 'email',
    label: 'Email Us',
    value: 'info@nisunajni.com.np',
    sub: 'We reply within 24–48 hours',
    href: 'mailto:info@nisunajni.com.np',
    icon: EnvelopeIcon,
  },
  {
    id: 'hours',
    label: 'Business Hours',
    value: 'Sun – Fri',
    sub: '10:00 AM – 4:00 PM NPT',
    icon: ClockIcon,
  },
];

const office = {
  city: 'Lalitpur, Kathmandu',
  country: 'Nepal',
  address: 'Sundhara, Dillibazar, Lalitpur, Kathmandu, Nepal',
  phone: '+977 9705995155, +977 9841586810',
  email: 'info@nisunajni.com.np',
  mapUrl:
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3532.5!2d85.324!3d27.67!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39eb1985614d4f4f%3A0x3d058f44d7d8b0b4!2sKathmandu%2C%20Nepal!5e0!3m2!1sen!2snp!4v1700000000000!5m2!1sen!2snp',
};

const serviceOptions = [
  { id: 'software', label: 'Custom Software' },
  { id: 'web', label: 'Web Development' },
  { id: 'mobile', label: 'Mobile Apps' },
  { id: 'cloud', label: 'Cloud Solutions' },
  { id: 'consulting', label: 'IT Consulting' },
  { id: 'cybersecurity', label: 'Cybersecurity' },
  { id: 'other', label: 'Other' },
];

const budgetOptions = [
  { id: 'less-than-5k', label: 'Under $5K' },
  { id: '5k-10k', label: '$5K – $10K' },
  { id: '10k-25k', label: '$10K – $25K' },
  { id: '25k-50k', label: '$25K – $50K' },
  { id: '50k-100k', label: '$50K – $100K' },
  { id: 'more-than-100k', label: '$100K+' },
];

const faqs = [
  {
    question: 'What should I include in my initial inquiry?',
    answer:
      'Share your project goals, timeline, budget range, and any technologies you prefer. The more context you provide, the more tailored our response will be.',
  },
  {
    question: 'How quickly will I receive a response?',
    answer:
      'We typically respond within 24–48 business hours. For urgent matters, note that in your message and we will prioritize your request.',
  },
  {
    question: 'Do you work with international clients?',
    answer:
      'Yes. We serve clients globally with flexible communication across time zones and remote delivery practices.',
  },
  {
    question: 'Can I book a free consultation first?',
    answer:
      'Absolutely. Mention that you would like a consultation in your message and we will schedule a call to discuss your requirements.',
  },
];

const inputClass =
  'w-full px-4 py-3 rounded-xl border border-slate-200/90 dark:border-gray-600 bg-white/80 dark:bg-gray-800/80 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/40 focus:border-[#2563EB] transition-all';

export default function Contact() {
  const [refHero, inViewHero] = useInView({ threshold: 0.1, triggerOnce: true });
  const [refForm, inViewForm] = useInView({ threshold: 0.08, triggerOnce: true });
  const [refMap, inViewMap] = useInView({ threshold: 0.08, triggerOnce: true });
  const [refFaq, inViewFaq] = useInView({ threshold: 0.08, triggerOnce: true });
  const [refCta, inViewCta] = useInView({ threshold: 0.1, triggerOnce: true });

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const serviceLabel = serviceOptions.find((o) => o.id === formData.service)?.label;
      const budgetLabel = budgetOptions.find((o) => o.id === formData.budget)?.label;

      await submitFormEmail({
        formName: 'Contact Message',
        fields: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          service: serviceLabel || formData.service,
          budget: budgetLabel || formData.budget,
          message: formData.message,
        },
      });

      setSubmitSuccess(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        budget: '',
        message: '',
      });
      setTimeout(() => setSubmitSuccess(false), 4000);
    } catch (error) {
      alert(error.message || 'Failed to send message. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="bg-white dark:bg-gray-900 overflow-x-hidden">
      {/* Hero */}
      <section
        ref={refHero}
        className="relative pt-28 pb-16 md:pt-32 md:pb-20 px-6 bg-gradient-to-b from-white via-slate-50/90 to-gray-100/80 overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-[28rem] h-[28rem] bg-blue-400/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-300/10 rounded-full blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inViewHero ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <SectionLabel>Contact Nisunajni Group</SectionLabel>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            <span className="text-slate-900 dark:text-white">Let&apos;s Start a </span>
            <span className="bg-gradient-to-r from-[#2563EB] via-blue-500 to-cyan-500 bg-clip-text text-transparent">
              Conversation
            </span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 dark:text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            Tell us about your project. Our team will respond with a tailored plan to help you achieve your technology goals.
          </p>
          <a
            href="#contact-form"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#2563EB] to-blue-500 text-white font-semibold rounded-xl hero-glow hover:-translate-y-0.5 transition-all"
          >
            Send a Message <ArrowRightIcon className="w-5 h-5" />
          </a>
        </motion.div>

        {/* Contact channels */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inViewHero ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="relative max-w-5xl mx-auto mt-14 grid sm:grid-cols-3 gap-4"
        >
          {contactChannels.map((ch, i) => {
            const Icon = ch.icon;
            const inner = (
              <div className="hero-glass-card rounded-2xl p-6 h-full hover:shadow-lg hover:shadow-blue-500/10 transition-all group">
                <div className="w-12 h-12 rounded-xl bg-[#2563EB]/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Icon className="w-6 h-6 text-[#2563EB]" />
                </div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 mb-1">
                  {ch.label}
                </p>
                <p className="font-bold text-slate-900 dark:text-white text-sm">{ch.value}</p>
                <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">{ch.sub}</p>
              </div>
            );
            return ch.href ? (
              <motion.a
                key={ch.id}
                href={ch.href}
                initial={{ opacity: 0, y: 16 }}
                animate={inViewHero ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 + i * 0.08 }}
                className="block"
              >
                {inner}
              </motion.a>
            ) : (
              <motion.div
                key={ch.id}
                initial={{ opacity: 0, y: 16 }}
                animate={inViewHero ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 + i * 0.08 }}
              >
                {inner}
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* Form + Office */}
      <section id="contact-form" ref={refForm} className="py-20 md:py-28 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-12 items-start">
            {/* Form — 3 cols */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={inViewForm ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="lg:col-span-3 hero-glass-card rounded-3xl p-8 md:p-10 shadow-lg"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-[#2563EB]/10 flex items-center justify-center">
                  <ChatBubbleLeftRightIcon className="w-5 h-5 text-[#2563EB]" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">Send Us a Message</h2>
              </div>
              <p className="text-slate-600 dark:text-gray-300 mb-8 text-sm">
                Fill out the form and our team will get back to you within 24–48 business hours.
              </p>

              <AnimatePresence>
                {submitSuccess && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mb-8 p-5 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 flex gap-4"
                  >
                    <CheckCircleIcon className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-emerald-800 dark:text-emerald-300">Message sent successfully</h3>
                      <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-1">
                        Thank you for reaching out. We&apos;ll be in touch shortly.
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-1.5">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className={inputClass}
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-1.5">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className={inputClass}
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-1.5">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={inputClass}
                      placeholder="you@company.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-1.5">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="+977 ..."
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-1.5">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Your organization"
                  />
                </div>

                <div>
                  <span className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">
                    Service <span className="text-red-500">*</span>
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {serviceOptions.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setFormData((p) => ({ ...p, service: opt.id }))}
                        className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                          formData.service === opt.id
                            ? 'bg-[#2563EB] text-white shadow-md shadow-blue-500/25'
                            : 'hero-glass-tag text-slate-600 dark:text-gray-300 hover:border-[#2563EB]/40'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <input type="hidden" name="service" value={formData.service} required />
                </div>

                <div>
                  <span className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Budget Range</span>
                  <div className="flex flex-wrap gap-2">
                    {budgetOptions.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setFormData((p) => ({ ...p, budget: opt.id }))}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                          formData.budget === opt.id
                            ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'
                            : 'hero-glass-tag text-slate-600 dark:text-gray-300'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-1.5">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className={`${inputClass} resize-none`}
                    placeholder="Tell us about your project, timeline, and goals..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !formData.service}
                  className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold text-white transition-all ${
                    isSubmitting || !formData.service
                      ? 'bg-slate-300 cursor-not-allowed'
                      : 'bg-gradient-to-r from-[#2563EB] to-blue-500 hero-glow hover:-translate-y-0.5'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message <PaperAirplaneIcon className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </motion.div>

            {/* Office sidebar — 2 cols */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={inViewForm ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-2 space-y-6"
            >
              <div>
                <SectionLabel>Visit Us</SectionLabel>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Our Office</h2>
                <p className="text-sm text-slate-600 dark:text-gray-300">
                  Stop by or reach out—we&apos;d love to meet you in person or virtually.
                </p>
              </div>

              <div className="hero-glass-card rounded-2xl overflow-hidden">
                <img src={officeImg} alt="Nisunajni office" className="w-full h-44 object-cover" />
                <div className="p-6 space-y-4">
                  <div className="flex gap-3">
                    <MapPinIcon className="w-5 h-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">
                        {office.city}, {office.country}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-gray-300 mt-1">{office.address}</p>
                    </div>
                  </div>
                  <a href="tel:+9779705995155" className="flex items-center gap-3 text-sm text-[#2563EB] hover:underline">
                    <PhoneIcon className="w-5 h-5" />
                    {office.phone}
                  </a>
                  <a href={`mailto:${office.email}`} className="flex items-center gap-3 text-sm text-[#2563EB] hover:underline">
                    <EnvelopeIcon className="w-5 h-5" />
                    {office.email}
                  </a>
                </div>
              </div>

              <div className="hero-glass-card rounded-2xl p-6">
                <h3 className="font-bold text-slate-900 dark:text-white mb-3">Why work with us?</h3>
                <ul className="space-y-3">
                  {[
                    'Free initial consultation',
                    'Enterprise-grade security practices',
                    'Agile delivery with clear milestones',
                    'Dedicated post-launch support',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-600 dark:text-gray-300">
                      <span className="text-[#2563EB] font-bold">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/services"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:gap-3 transition-all"
                >
                  Explore services <ArrowRightIcon className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section ref={refMap} className="py-12 md:py-16 px-6 bg-slate-50/80 dark:bg-gray-800/50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inViewMap ? { opacity: 1, y: 0 } : {}}
          className="max-w-7xl mx-auto"
        >
          <div className="hero-glass-card rounded-3xl overflow-hidden shadow-lg">
            <div className="px-6 py-5 border-b border-slate-200/80 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">Find Us in Kathmandu</h3>
                <p className="text-sm text-slate-500 dark:text-gray-400">{office.address}</p>
              </div>
              <a
                href="https://maps.google.com/?q=Sundhara+Dillibazar+Lalitpur+Kathmandu+Nepal"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-[#2563EB] hover:underline inline-flex items-center gap-1"
              >
                Open in Google Maps <ArrowRightIcon className="w-4 h-4" />
              </a>
            </div>
            <div className="h-80 md:h-96 w-full">
              <iframe
                src={office.mapUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Nisunajni Group office location"
                className="w-full h-full"
              />
            </div>
          </div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section ref={refFaq} className="py-20 md:py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={inViewFaq ? { opacity: 1, y: 0 } : {}} className="text-center mb-12">
            <SectionLabel>FAQ</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              Common <span className="text-[#2563EB]">Questions</span>
            </h2>
          </motion.div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 12 }}
                animate={inViewFaq ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.06 }}
                className="hero-glass-card rounded-2xl overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex justify-between items-center text-left gap-4"
                >
                  <span className="font-semibold text-slate-900 dark:text-white">{faq.question}</span>
                  <ChevronDownIcon
                    className={`w-5 h-5 text-[#2563EB] flex-shrink-0 transition-transform duration-300 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-5 text-slate-600 dark:text-gray-300 text-sm leading-relaxed">{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        ref={refCta}
        className="py-20 px-6 bg-gradient-to-r from-blue-600 to-purple-600 relative overflow-hidden"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inViewCta ? { opacity: 1, y: 0 } : {}}
          className="max-w-4xl mx-auto text-center relative z-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl text-white/90 mb-10">
            Let&apos;s build secure, scalable solutions tailored to your goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#contact-form"
              className="px-8 py-4 bg-white text-blue-600 font-medium rounded-lg shadow-lg hover:bg-gray-100 transition"
            >
              Get Started
            </a>
            <Link to="/services" className="px-8 py-4 border-2 border-white text-white font-medium rounded-lg hover:bg-white/10 transition">
              Explore Services
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
